import type { FieldValues } from "react-hook-form";
import { useUserDivisionAccess } from "@app/session-data/user-divisions";
import { resolveDefaultDivisionCode } from "@app/session-defaults/resolveDefaultDivisionCode";
import { useUserStore } from "@app/store/useUserStore";
import type { ApiDivisionFilter } from "@src/api/dictionaries/divisions";
import type { FormSeedResult, FormSeedStatus } from "@shared/ui/form-panel";

type DivisionDefaultFilter = Pick<ApiDivisionFilter, "Types" | "AvailableFor">;

/** Результат резолва default-подразделения. */
export interface DivisionDefaultResult {
    /** Код подходящего подразделения; `null`, если подходящего назначения нет. */
    value: number | null;
    /** Состояние server-side проверки. */
    status: FormSeedStatus;
    /** `true`, когда cached capability обновляется в фоне. */
    isRefreshing: boolean;
    /** Ошибка server-side проверки. */
    error: unknown | null;
}

/**
 * Опции резолва default-подразделения для seed фильтра. \
 * Обычно совпадают с `flt.Types` у `DivisionSearchSelect`.
 */
export interface ResolveDivisionDefaultOptions {
    /**
     * Constraint, совпадающий с `flt` у `DivisionSearchSelect`. \
     * `Types` проверяется локально; `"Create"` — через cached session capability. \
     * `"Read"` эквивалентен общему списку и отдельной проверки не требует.
     */
    filter?: DivisionDefaultFilter;
    /**
     * Управляет server-side проверкой seed. \
     * Используйте `false`, только если seed не нужен ни для initial values, ни для reset.
     *
     * TODO(forms/future): при перепроектировании пакета форм учесть lazy/gated seed:
     * server-side автозаполнение (Create-capability, а в перспективе — дефолты, настроенные
     * в Омега продакшен) стоит пропускать, когда поле уже покрыто submission memory
     * (например, через sync peek `hasSubmissionMemory(key)`), чтобы не гонять лишние запросы.
     * @default true
     */
    enabled?: boolean;
}

/**
 * Резолвит default-код подразделения из назначений текущего пользователя.
 *
 * При `AvailableFor: "Create"` пересекает назначения с cached server capability.
 * Во время первоначальной проверки возвращает `status: "pending"`.
 *
 * @param options constraint по типам; без опций — любое первое подразделение
 * @returns результат подготовки кода и статус инициализации
 */
export function useResolvedDefaultDivision(options?: ResolveDivisionDefaultOptions): DivisionDefaultResult {
    const divisions = useUserStore((state) => state.currentUser?.Divisions ?? []);
    const filter = options?.filter;
    const shouldValidateCreateAccess =
        filter?.AvailableFor === "Create" && divisions.length > 0 && options?.enabled !== false;
    const accessQuery = useUserDivisionAccess(shouldValidateCreateAccess);

    if (shouldValidateCreateAccess && accessQuery.data == null && accessQuery.isPending) {
        return { status: "pending", value: null, isRefreshing: false, error: null };
    }

    if (shouldValidateCreateAccess && accessQuery.data == null && accessQuery.isError) {
        return { status: "error", value: null, isRefreshing: false, error: accessQuery.error };
    }

    const allowedCodes =
        shouldValidateCreateAccess && accessQuery.data != null ? new Set(accessQuery.data.createDivisionCodes) : undefined;
    const divisionCode = resolveDefaultDivisionCode(divisions, filter?.Types, allowedCodes);

    return {
        status: "success",
        value: divisionCode,
        isRefreshing: shouldValidateCreateAccess && accessQuery.isFetching,
        error: accessQuery.isRefetchError ? accessQuery.error : null,
    };
}

/**
 * Формирует partial seed для form-поля подразделения из назначений пользователя.
 *
 * @param fieldName имя form-поля (`divisionCode`, `receiverCode`, ...)
 * @param options constraint по типам; обычно тот же, что `flt.Types` у select
 *
 * @example
 * ```ts
 * useDivisionFilterSeed("divisionCode", { filter: { Types: [DivisionType.Склад] } })
 * useDivisionFilterSeed("receiverCode", { filter: { AvailableFor: "Create" } })
 * useDivisionFilterSeed("senderCode") // без фильтра по типу
 * ```
 */
export function useDivisionFilterSeed<TForm extends FieldValues>(
    fieldName: keyof TForm & string,
    options?: ResolveDivisionDefaultOptions,
): FormSeedResult<TForm> {
    const defaultResult = useResolvedDefaultDivision(options);
    const defaultDivisionCode = defaultResult.value;

    if (defaultResult.status !== "success" || defaultDivisionCode == null) {
        return {
            status: defaultResult.status,
            values: undefined,
            error: defaultResult.error,
        };
    }

    // TODO(types): remove assertion — TS не выводит Partial<TForm> из computed key.
    // План: helper `setPartialField(seed, fieldName, value)` с generic-ограничением значения поля.
    return {
        status: "success",
        values: { [fieldName]: defaultDivisionCode } as Partial<TForm>,
        error: null,
    };
}
